import moment from "moment";
import {
  IconAlertTriangle,
  IconCalendarOff,
  IconChevronCompactUp,
  IconChevronUp,
  IconChevronsDown,
  IconChevronsUp,
  IconFlame,
  IconMinus,
  IconQuestionMark,
} from "@tabler/icons-react";

export const SAMPLE_STATUSES = [
  "Not Yet Actioned",
  "Acquired",
  "Scheduled",
  "Captured",
  "Post Production",
  "Distributed",
  "Returned",
];

// Urgency columns on the dashboard, most urgent first. Names match the
// PriorityTypesMap labels (P0-P8, Low, Error); `range` is the OTB window.
export const URGENCY_LEVELS = [
  { name: "P0", range: "Today or past" },
  { name: "P1", range: "< 1 Week" },
  { name: "P2", range: "1-2 Weeks" },
  { name: "P3", range: "2-3 Weeks" },
  { name: "P4", range: "3-4 Weeks" },
  { name: "P5", range: "4-5 Weeks" },
  { name: "P6", range: "5-6 Weeks" },
  { name: "P7", range: "6-7 Weeks" },
  { name: "P8", range: "7-8 Weeks" },
  { name: "Low", range: "8+ Weeks" },
  { name: "Error", range: "No OTB" },
];

// Known errant scan reasons. The dashboard always lists these (at 0 when a
// week has none) so the labels stay put; any other reason in the data is
// added after them.
export const ERRANT_REASONS = [
  "No Barcode Match",
  "Duplicate Scan",
  "Style Discontinued",
  "Wrong Search Code",
  "Missing in Feed",
  "No Barcode",
];

// Workflow status derived from an inventory record. Values match the dashboard
// labels (SAMPLE_STATUSES / STATUS_COLORS) so the two stay in step.
export const InventoryStatusTypes = {
  NOT_YET_ACTIONED: { value: "Not Yet Actioned", label: "Not Yet Actioned" },
  SAMPLE_ACQUIRED: { value: "Acquired", label: "Sample Acquired" },
  SESSION_SCHEDULED: { value: "Scheduled", label: "Session Scheduled" },
  CAPTURED: { value: "Captured", label: "Captured" },
  POST_PRODUCTION: { value: "Post Production", label: "Post Production" },
  DISTRIBUTED: { value: "Distributed", label: "Distributed" },
};

// Priority from how far away the OTB date is (P0 = today or past, then one
// level per week up to P8 = under 8 weeks; Low = 8+ weeks). ERROR covers a
// missing or unparseable OTB. Colours follow the spec: critical dark red,
// overdue red, urgent light red, medium orange. They're the source for the
// dashboard's URGENCY_COLORS (inventoryColors.js imports them from here, not
// the other way round, as inventoryColors pulls in utils/helper).
const PRIORITY_ICON_SIZE = 14;
const critical = { color: "red.9", icon: <IconFlame size={PRIORITY_ICON_SIZE} /> };
const overdue = { color: "red.6", icon: <IconAlertTriangle size={PRIORITY_ICON_SIZE} /> };
const urgent = { color: "red.4", icon: <IconChevronsUp size={PRIORITY_ICON_SIZE} /> };
const medium = { color: "orange.6", icon: <IconChevronUp size={PRIORITY_ICON_SIZE} /> };
export const PriorityTypesMap = {
  P0: { value: "P0", label: "P0", description: "Critical: OTB is today or in the past", ...critical },
  P1: { value: "P1", label: "P1", description: "Overdue: OTB is less than 1 week away", ...overdue },
  P2: { value: "P2", label: "P2", description: "Overdue: OTB is less than 2 weeks away", ...overdue },
  P3: { value: "P3", label: "P3", description: "Urgent: OTB is less than 3 weeks away", ...urgent },
  P4: { value: "P4", label: "P4", description: "Urgent: OTB is less than 4 weeks away", ...urgent },
  P5: { value: "P5", label: "P5", description: "Medium: OTB is less than 5 weeks away", ...medium },
  P6: { value: "P6", label: "P6", description: "Medium: OTB is less than 6 weeks away", ...medium },
  P7: { value: "P7", label: "P7", description: "Medium: OTB is less than 7 weeks away", ...medium },
  P8: { value: "P8", label: "P8", description: "Medium: OTB is less than 8 weeks away", ...medium },
  LOW: { value: "LOW", label: "Low", description: "OTB is 8+ weeks in the future", color: "green", icon: <IconChevronsDown size={PRIORITY_ICON_SIZE} /> },
  ERROR: { value: "ERROR", label: "Error", description: "OTB is unreadable or null", color: "gray", icon: <IconQuestionMark size={PRIORITY_ICON_SIZE} /> },
};

// Priority type for a number of days until the OTB date (0 or less = today
// or past). Pn means under n weeks away, so exactly 1 week is P2, 8 weeks Low.
// Shared by the list (getPriority) and the dashboard (urgency), so both agree.
export const priorityForDaysUntilOtb = (daysUntilOtb) => {
  if (daysUntilOtb === null || daysUntilOtb === undefined || Number.isNaN(daysUntilOtb)) {
    return PriorityTypesMap.ERROR;
  }
  if (daysUntilOtb <= 0) return PriorityTypesMap.P0;
  const week = Math.floor(daysUntilOtb / 7) + 1;
  return week <= 8 ? PriorityTypesMap[`P${week}`] : PriorityTypesMap.LOW;
};
// export const PriorityTypesMap = {
//   CRITICAL: {
//     value: "Critical",
//     label: "Critical",
//     color: "red.8",
//     icon: <IconFlame size={PRIORITY_ICON_SIZE} />,
//   },
//   OVERDUE: {
//     value: "Overdue",
//     label: "Overdue",
//     color: "red.6",
//     icon: <IconAlertTriangle size={PRIORITY_ICON_SIZE} />,
//   },
//   URGENT: {
//     value: "Urgent",
//     label: "Urgent",
//     color: "red.4",
//     icon: <IconChevronsUp size={PRIORITY_ICON_SIZE} />,
//   },
//   MEDIUM: {
//     value: "Medium",
//     label: "Medium",
//     color: "red.2",
//     icon: <IconChevronUp size={PRIORITY_ICON_SIZE} />,
//   },
//   LOW: {
//     value: "Low",
//     label: "Low",
//     color: "gray.3",
//     icon: <IconMinus size={PRIORITY_ICON_SIZE} />,
//   },
//   ERROR: {
//     value: "Error",
//     label: "No OTB",
//     color: "gray.6",
//     icon: <IconCalendarOff size={PRIORITY_ICON_SIZE} />,
//   },
// };

const OTB_FORMATS = [
  "YYYY-MM-DD",
  "YYYY-MM-DD-HH-mm-ss",
  "YYYY-MM-DD-HH-mm-ss-SSS",
  "DD/MM/YYYY",
  "D/M/YYYY",
  moment.ISO_8601,
];

// Strict parse of a sample's OTB date; null when missing or invalid.
export const parseSampleOtbMoment = (value) => {
  if (!value) return null;
  const date = moment(String(value).trim(), OTB_FORMATS, true);
  return date.isValid() ? date : null;
};

// Same strict parse for any inventory record date (created, shootDate,
// distributionDates, scan dates), so the dashboard reads dates exactly as the
// list and derivedStatus do.
export const parseInventoryDate = parseSampleOtbMoment;

// List fields can arrive as arrays, JSON array strings ("[]") or
// comma-separated strings. A raw "[]" string must not count as present.
export const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== "string" || !value.trim()) return [];
  const text = value.trim();
  if (text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (err) {
      // Not JSON - treat it as a comma-separated string below.
    }
  }
  return text
    .split(",")
    .map((entry) => entry.replace(/[[\]"']/g, "").trim())
    .filter(Boolean);
};

// Workflow status for a raw record (shared by the list and the dashboard).
export const getDerivedStatus = (val) => {
  const samplesAcquired = parseInt(val.sampleCount) > 0;
  const sessionScheduled = toList(val.sessionCodes).length > 0;
  // Captured once it has photographs (assets).
  const imagesCaptured = parseInt(val.photographyCount) > 0;
  const postProduction = parseInt(val.taskCount) > 0;
  const distributed = toList(val.distributionDates).length > 0;

  if (distributed) return InventoryStatusTypes.DISTRIBUTED.value;
  if (postProduction) return InventoryStatusTypes.POST_PRODUCTION.value;
  if (imagesCaptured) return InventoryStatusTypes.CAPTURED.value;
  if (sessionScheduled) return InventoryStatusTypes.SESSION_SCHEDULED.value;
  if (samplesAcquired) return InventoryStatusTypes.SAMPLE_ACQUIRED.value;
  return InventoryStatusTypes.NOT_YET_ACTIONED.value;
};

const getPriority = (val, today) => {
  const otbDate = parseSampleOtbMoment(val.OTB ?? val.otb);
  if (!otbDate) return PriorityTypesMap.ERROR.value;

  const daysUntilOtb = otbDate
    .clone()
    .startOf("day")
    .diff(today.clone().startOf("day"), "days");

  return priorityForDaysUntilOtb(daysUntilOtb).value;
};

// Lifecycle steps in workflow order, for the status timeline. Each step says
// what evidence on the record marks it; the record only carries dates for
// some steps (received, shoot, distribution), the rest show what's known.
const LIFECYCLE_ORDER = [
  InventoryStatusTypes.NOT_YET_ACTIONED,
  InventoryStatusTypes.SAMPLE_ACQUIRED,
  InventoryStatusTypes.SESSION_SCHEDULED,
  InventoryStatusTypes.CAPTURED,
  InventoryStatusTypes.POST_PRODUCTION,
  InventoryStatusTypes.DISTRIBUTED,
];

const firstDistribution = (val) =>
  toList(val.distributionDates)
    .map(String)
    .sort()
    .shift() ?? null;

/**
 * The record's lifecycle as timeline steps, ending at its derivedStatus.
 * Steps up to and including the current status count as reached, even when
 * an earlier step left no trace (e.g. distributed with no sample count).
 * @returns {{ steps: Array<{type, title, date, detail}>, activeIndex: number }}
 */
export const getInventoryLifecycle = (val, today = moment()) => {
  const current = val.derivedStatus ?? getDerivedStatus(val);
  const activeIndex = LIFECYCLE_ORDER.findIndex((type) => type.value === current);

  const sessions = toList(val.sessionCodes);
  const taskCount = parseInt(val.taskCount) || 0;
  const sampleCount = parseInt(val.sampleCount) || 0;
  const shootDate = val.shootDate ? moment(val.shootDate, "YYYY-MM-DD", true) : null;
  const shootPlanned = Boolean(shootDate?.isValid() && !shootDate.isBefore(today, "day"));

  const steps = LIFECYCLE_ORDER.map((type) => {
    switch (type) {
      case InventoryStatusTypes.NOT_YET_ACTIONED:
        return {
          type,
          title: "Received",
          date: val.scanned && val.scanned !== "false" ? val.scanned : val.created ?? null,
          detail: val.scanLocation ? `Scanned at ${val.scanLocation}` : null,
        };
      case InventoryStatusTypes.SAMPLE_ACQUIRED:
        return {
          type,
          title: type.label,
          date: null,
          detail: sampleCount ? `${sampleCount} sample${sampleCount === 1 ? "" : "s"}` : null,
        };
      case InventoryStatusTypes.SESSION_SCHEDULED:
        return {
          type,
          title: type.label,
          date: null,
          detail: sessions.length ? sessions.join(", ") : null,
        };
      case InventoryStatusTypes.CAPTURED:
        return {
          type,
          title: type.label,
          date: val.captured || null,
          detail: shootPlanned ? "Shoot planned" : null,
        };
      case InventoryStatusTypes.POST_PRODUCTION:
        return {
          type,
          title: type.label,
          date: null,
          detail: taskCount ? `${taskCount} task${taskCount === 1 ? "" : "s"}` : null,
        };
      case InventoryStatusTypes.DISTRIBUTED:
      default:
        return {
          type,
          title: type.label,
          date: firstDistribution(val),
          detail: null,
        };
    }
  });

  return { steps, activeIndex };
};

// Adds derivedStatus and priority to each raw GET /inventory record.
export const deriveInventories = (items = [], today = moment()) =>
  items.map((val) => ({
    ...val,
    derivedStatus: getDerivedStatus(val),
    priority: getPriority(val, today),
  }));

// GET /inventory returns raw records; add the same fields the WIP schedule
// service adds (id, styleNumber...) so the shared columns and filters
// (filterSamples) work. status is the derivedStatus the slice works out on
// fetch. Copies each record as the store's are frozen. Used by the list and
// the dashboard, so both filter the same way.
export const normalizeInventory = (item) => ({
  ...item,
  status: item.derivedStatus ?? "",
  id: item.seasonSort,
  styleNumber: item.seasonSort,
  dept: item.Dept,
  name: item.Name,
  otbDate: item.OTB,
  size: item.Size,
  // The list columns read these lowercase names; fall back to the raw fields.
  otb: item.otb ?? item.OTB,
  deptName: item.deptName ?? item.DeptName,
});

// Records whose `created` falls within [start, end] (whole days; end defaults
// to the start day). Records with no valid created are left out. No start
// means no date filter.
export const filterByCreated = (records = [], [start, end] = []) => {
  if (!start) return records;
  const from = moment(start).startOf("day");
  const to = end ? moment(end).endOf("day") : from.clone().endOf("day");
  return records.filter((item) => {
    const created = item.created ? moment(item.created) : null;
    return Boolean(created?.isValid()) && !created.isBefore(from) && !created.isAfter(to);
  });
};
